/**
 * Pure utility functions for the expiring-soon hint shown after login.
 * No React, no i18next runtime — Jest-friendly.
 */

import { ExpiringSoonRequestDTO } from "@/api/data-contracts";
import { AppPaths } from "@/app/AppPaths";
import { convertToThreeDigitString } from "@/lib/utils";

/**
 * Returns true when the expiring-soon hint should be shown to the user.
 * The hint is shown whenever the list contains at least one entry.
 *
 * @param requests - list of expiring-soon requests returned by the backend, or null/undefined when unavailable
 */
export function shouldShowExpiringHints(requests: ExpiringSoonRequestDTO[] | null | undefined): boolean {
    return Array.isArray(requests) && requests.length > 0;
}

/**
 * Returns a human-readable display identifier for a single expiring-soon request.
 * Mirrors the format used by the request page: companyNumber (zero-padded to 3 digits) + "-" + customerReferenceNumber.
 * Fallback chain: companyNumber+customerReferenceNumber → customerReferenceNumber alone → String(id) → "".
 *
 * Note: convertToThreeDigitString returns null for companyNumber values outside 0–999.
 * In that edge case the label becomes "null-<customerReferenceNumber>" — matching the request-page behavior.
 *
 * @param request - expiring-soon request DTO
 */
export function buildExpiringRequestLabel(request: ExpiringSoonRequestDTO): string {
    const { companyNumber, customerReferenceNumber, id } = request;
    if (companyNumber !== undefined && companyNumber !== null && customerReferenceNumber !== undefined && customerReferenceNumber !== null) {
        return `${convertToThreeDigitString(companyNumber)}-${customerReferenceNumber}`;
    }
    if (customerReferenceNumber !== undefined && customerReferenceNumber !== null) {
        return customerReferenceNumber;
    }
    if (id !== undefined && id !== null) {
        return String(id);
    }
    return "";
}

/**
 * Returns the route PATH (without a leading "#") for the internal-request detail page of a given request id.
 * The path is used by the snackbar deep-link via `window.location.hash = requestDeepLinkPath(id)`.
 * Assigning a "#"-less string to `window.location.hash` is correct: the browser normalises
 * the value to `#/internal-request/<id>`, which is the intended hash-router destination.
 *
 * @param id - request id
 */
export function requestDeepLinkPath(id: number): string {
    return AppPaths.INTERNAL_REQUEST_fn(id);
}
